// Port of python/New/AstraLib/get_structure.py (VxGeom)
// Each "vec" row: [Sx,Sy,Sz, Dx,Dy,Dz, Ux,Uy,Uz, Vx,Vy,Vz]

const deg2rad = (d) => (d * Math.PI) / 180;

/**
 * @param {object} p params: sod, sdd, angles (array, deg), tiltX (deg), offsetU, offsetV, detPitch
 * @returns {Float64Array[]} array of 12-element rows, one per angle
 */
export function computeDefaultGeom(p) {
  const tiltXRad = deg2rad(p.tiltX);
  const tiltYRad = deg2rad(p.tiltY ?? 0);

  const effSod = p.sod;
  const effOdd = p.sdd - p.sod;
  const effPixel = p.detPitch;

  const sinTx = Math.sin(tiltXRad);
  const cosTx = Math.cos(tiltXRad);
  const sinTy = Math.sin(tiltYRad);
  const cosTy = Math.cos(tiltYRad);

  return p.angles.map((angleDeg) => {
    const phi = -deg2rad(angleDeg);
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // R = Rx(-tiltX) * Ry(tiltY) * Rz(-phi)
    const r00 = cosTy * cosPhi;
    const r01 = cosTy * sinPhi;
    const r02 = -sinTy;

    const r10 = sinTx * sinTy * cosPhi - cosTx * sinPhi;
    const r11 = sinTx * sinTy * sinPhi + cosTx * cosPhi;
    const r12 = sinTx * cosTy;

    const r20 = cosTx * sinTy * cosPhi + sinTx * sinPhi;
    const r21 = cosTx * sinTy * sinPhi - sinTx * cosPhi;
    const r22 = cosTx * cosTy;

    const vec = new Float64Array(12);

    // S = -row2 * effSod
    vec[0] = -effSod * r20;
    vec[1] = -effSod * r21;
    vec[2] = -effSod * r22;

    // D = +row2 * effOdd
    vec[3] = effOdd * r20;
    vec[4] = effOdd * r21;
    vec[5] = effOdd * r22;

    // U = +row0 * effPixel
    vec[6] = effPixel * r00;
    vec[7] = effPixel * r01;
    vec[8] = effPixel * r02;

    // V = -row1 * effPixel
    vec[9] = -effPixel * r10;
    vec[10] = -effPixel * r11;
    vec[11] = -effPixel * r12;

    // detector offsets along U/V
    vec[3] += p.offsetU * vec[6] + p.offsetV * vec[9];
    vec[4] += p.offsetU * vec[7] + p.offsetV * vec[10];
    vec[5] += p.offsetU * vec[8] + p.offsetV * vec[11];

    return vec;
  });
}

export function computePlanarGeom(p) {
  const tiltXRad = deg2rad(p.tiltX);

  const effSod = p.sod;
  const effOdd = p.sdd - p.sod;
  const effPixel = p.detPitch;
  const tanTx = Math.tan(tiltXRad);

  return p.angles.map((angleDeg) => {
    const phi = -deg2rad(angleDeg);
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    const vec = new Float64Array(12);

    // Source: axial SOD plus tilt-induced XY shift
    vec[0] = -effSod * tanTx * cosPhi;
    vec[1] = -effSod * tanTx * sinPhi;
    vec[2] = -effSod;

    // Detector center: axial ODD plus matching tilt-induced XY shift
    vec[3] = effOdd * tanTx * cosPhi;
    vec[4] = effOdd * tanTx * sinPhi;
    vec[5] = effOdd;

    // Flat detector basis, constant for all projections
    vec[6] = effPixel;
    vec[7] = 0;
    vec[8] = 0;

    vec[9] = 0;
    vec[10] = -effPixel;
    vec[11] = 0;

    vec[3] += p.offsetU * vec[6] + p.offsetV * vec[9];
    vec[4] += p.offsetU * vec[7] + p.offsetV * vec[10];
    vec[5] += p.offsetU * vec[8] + p.offsetV * vec[11];

    return vec;
  });
}

/**
 * Z-orbit geometry with a world-X-stabilized detector (no roll).
 *
 * Source/detector centers follow the same inclined orbit as computeDefaultGeom,
 * but instead of a rigid rotation for the detector basis, U is obtained by
 * projecting world +X onto the detector plane every angle. This removes
 * detector roll: the edge that starts on the right stays the right edge
 * throughout the orbit. V completes the orthonormal basis via cross product.
 */
export function computeGimballLockGeom(p) {
  const tx = deg2rad(p.tiltX);
  const ty = deg2rad(p.tiltY ?? 0);
  const sx = Math.sin(tx);
  const cx = Math.cos(tx);
  const sy = Math.sin(ty);
  const cy = Math.cos(ty);

  // Inclined detector normal at angle zero; its orbit is around world Z.
  const n0 = [cx * sy, -sx, cx * cy];

  const worldRight = [1, 0, 0];

  return p.angles.map((angleDeg) => {
    const phi = -deg2rad(angleDeg);
    const cp = Math.cos(phi);
    const sp = Math.sin(phi);

    // Rotate n0 about world Z by phi.
    const normal = [
      cp * n0[0] - sp * n0[1],
      sp * n0[0] + cp * n0[1],
      n0[2],
    ];

    // Project fixed world-right into the detector plane (no-roll constraint).
    const dot = normal[0] * worldRight[0] + normal[1] * worldRight[1] + normal[2] * worldRight[2];
    let u = [
      worldRight[0] - normal[0] * dot,
      worldRight[1] - normal[1] * dot,
      worldRight[2] - normal[2] * dot,
    ];
    const uNorm = Math.hypot(u[0], u[1], u[2]);
    if (uNorm < 1e-12) {
      throw new Error("Gimbal U axis is singular when detector normal aligns with world X");
    }
    u = [u[0] / uNorm, u[1] / uNorm, u[2] / uNorm];

    // v = u x normal
    const v = [
      u[1] * normal[2] - u[2] * normal[1],
      u[2] * normal[0] - u[0] * normal[2],
      u[0] * normal[1] - u[1] * normal[0],
    ];

    const vec = new Float64Array(12);
    vec[0] = -p.sod * normal[0];
    vec[1] = -p.sod * normal[1];
    vec[2] = -p.sod * normal[2];

    vec[3] = (p.sdd - p.sod) * normal[0];
    vec[4] = (p.sdd - p.sod) * normal[1];
    vec[5] = (p.sdd - p.sod) * normal[2];

    vec[6] = p.detPitch * u[0];
    vec[7] = p.detPitch * u[1];
    vec[8] = p.detPitch * u[2];

    vec[9] = p.detPitch * v[0];
    vec[10] = p.detPitch * v[1];
    vec[11] = p.detPitch * v[2];

    vec[3] += p.offsetU * vec[6] + p.offsetV * vec[9];
    vec[4] += p.offsetU * vec[7] + p.offsetV * vec[10];
    vec[5] += p.offsetU * vec[8] + p.offsetV * vec[11];

    return vec;
  });
}

export function detectorCorners(vec, halfU, halfV) {
  const D = [vec[3], vec[4], vec[5]];
  const U = [vec[6], vec[7], vec[8]];
  const V = [vec[9], vec[10], vec[11]];

  const cornersUV = [
    [-halfU, -halfV],
    [halfU, -halfV],
    [halfU, halfV],
    [-halfU, halfV],
  ];

  return cornersUV.map(([u, v]) => [
    D[0] + u * U[0] + v * V[0],
    D[1] + u * U[1] + v * V[1],
    D[2] + u * U[2] + v * V[2],
  ]);
}

/** Project detector corners through S onto plane z = zPlane. Returns array of [x,y] or null. */
export function footprintOnPlane(vec, halfU, halfV, zPlane = 0) {
  const S = [vec[0], vec[1], vec[2]];
  const corners = detectorCorners(vec, halfU, halfV);

  const pts = [];
  for (const P of corners) {
    const dz = P[2] - S[2];
    if (Math.abs(dz) < 1e-12) return null;
    const t = (zPlane - S[2]) / dz;
    if (t <= 0) return null;
    pts.push([S[0] + t * (P[0] - S[0]), S[1] + t * (P[1] - S[1])]);
  }
  return pts;
}
